import axios from "axios"
import jwtDecode from "jwt-decode"
import {
  removeUserInfo,
  setForgotPasswordResponseErrorMessage,
  setForgotPasswordStatus,
  setLoginResponseErrorMessage,
  setLoginStatus,
  setResetPasswordResponseErrorMessage,
  setResetPasswordStatus,
  setUserInfo,
} from "redux/slices/authorization"
import { Status } from "shared/types/status"
import { User } from "shared/types/user"
import config from "../../config"
import type { EPIDispatch, RootState } from "../store"

export function login(email: string, password: string, rememberMe: boolean) {
  return async function login(dispatch: EPIDispatch, getState: RootState) {
    dispatch({ type: setLoginStatus.type, payload: Status.loading })
    axios
      .post(`${config.apiGateway.URL}/auth/login`, {
        email,
        password,
      })
      .then(function (response) {
        const token: string = response.headers["x-amzn-remapped-authorization"]
        const finalToken = token.slice(
          token.indexOf("=") + 1,
          token.indexOf(";")
        )

        const expiresIn = 24 * 60 * 60 * 1000 // 1 day in milliseconds
        const expiryTime = new Date().getTime() + expiresIn

        const user: User = jwtDecode(finalToken)

        localStorage.setItem("token", finalToken)
        localStorage.setItem("token_expiry", expiryTime.toString())

        axios.defaults.headers.common["authorization"] = finalToken
        user.token = finalToken
        dispatch({ type: setUserInfo.type, payload: user })
        dispatch({ type: setLoginStatus.type, payload: Status.success })
      })
      .catch(function (error) {
        dispatch({ type: setLoginStatus.type, payload: Status.failed })

        if (error.response !== undefined) {
          if (error.response.data !== undefined) {
            dispatch({
              type: setLoginResponseErrorMessage.type,
              payload: error.response.data.message,
            })
          }
        } else dispatch({ type: setLoginResponseErrorMessage.type, payload: "Unknown error" })
      })
  }
}

export function checkAuth(navigate: (path: string) => void) {
  return async function checkAuthThunk(
    dispatch: EPIDispatch,
    getState: RootState
  ): Promise<void> {
    dispatch({ type: setLoginStatus.type, payload: Status.loading })

    // Retrieve token from local storage
    const token = localStorage.getItem("token")

    if (!token) {
      dispatch({ type: setLoginStatus.type, payload: Status.failed })
      navigate("/signin")

      return
    }

    axios
      .get(`${config.apiGateway.URL}/auth/check-auth`)
      .then((response) => {
        // Even if the endpoint doesn't return a body, we expect a 200 status
        if (response.status === 200) {
          const finalToken = localStorage.getItem("token")
          const user: User = jwtDecode(finalToken)
          user.token = finalToken

          dispatch({ type: setUserInfo.type, payload: user })
          dispatch({ type: setLoginStatus.type, payload: Status.success })
        } else {
          localStorage.clear()
          navigate("/signin")
        }
      })
      .catch((error) => {
        localStorage.clear()
        navigate("/signin")
      })
  }
}

export function logout() {
  return (dispatch: EPIDispatch) => {
    localStorage.removeItem("token")
    localStorage.removeItem("token_expiry")
    dispatch({ type: removeUserInfo.type })
    dispatch({ type: setLoginStatus.type, payload: Status.failed })
    axios.defaults.headers.common["authorization"] = "allow"
  }
}

export function forgotPassword(email: string) {
  return async function forgotPassword(
    dispatch: EPIDispatch,
    getState: RootState
  ) {
    //This url should be hidden in an env variable
    axios
      .post(`${config.apiGateway.URL}/email/reset`, {
        email,
      })
      .then(function (response) {
        //response can be used to set user. Using dummy user for now
        // const attributes = JSON.parse(response.data.message)
        // const attributes = response.data.message
        // dispatch({ type: setUserInfo.type, payload: attributes.Attributes })
        dispatch({
          type: setForgotPasswordStatus.type,
          payload: Status.success,
        })
      })
      .catch(function (error) {
        dispatch({ type: setForgotPasswordStatus.type, payload: Status.failed })

        if (error.response !== undefined) {
          if (error.response.data !== undefined) {
            dispatch({
              type: setForgotPasswordResponseErrorMessage.type,
              payload: error.response.data.message,
            })
          }
        } else dispatch({ type: setForgotPasswordResponseErrorMessage.type, payload: "Unknown error" })
      })
  }
}

export function resetPassword(email: string, password: string) {
  return async function resetPassword(
    dispatch: EPIDispatch,
    getState: RootState
  ) {
    //This url should be hidden in an env variable
    //reset token needs to be passed in here once that's added into the API
    axios
      .put(`${config.apiGateway.URL}/auth/password`, {
        email,
        password,
      })
      .then(function (response) {
        //response can be used to set user. Using dummy user for now
        // const attributes = response.data.message
        // dispatch({ type: setUserInfo.type, payload: attributes.Attributes })
        dispatch({ type: setResetPasswordStatus.type, payload: Status.success })
      })
      .catch(function (error) {
        dispatch({ type: setResetPasswordStatus.type, payload: Status.failed })

        if (error.response !== undefined) {
          if (error.response.data !== undefined) {
            dispatch({
              type: setResetPasswordResponseErrorMessage.type,
              payload: error.response.data.message,
            })
          }
        } else dispatch({ type: setResetPasswordResponseErrorMessage.type, payload: "Unknown error" })
      })
  }
}

export function refreshToken(token: string) {
  return async function refreshToken(
    dispatch: EPIDispatch,
    getState: RootState
  ) {
    axios
      .post(
        `${config.apiGateway.URL}/auth/refresh`,
        {
          token,
        },
        {
          headers: {
            authorization: "allow",
          },
        }
      )
      .then((response) => {
        if (response.status === 200) {
          const token: string =
            response.headers["x-amzn-remapped-authorization"]
          const finalToken = token.slice(
            token.indexOf("=") + 1,
            token.indexOf(";")
          )

          const user: User = jwtDecode(finalToken)
          localStorage.setItem("token", finalToken)

          axios.defaults.headers.common["authorization"] = finalToken
          user.token = finalToken
          dispatch({ type: setUserInfo.type, payload: user })
        }
      })
      .catch((error) => {
        console.error(error)
        // error
        /* const logoutThunk = logout()
                dispatch(logoutThunk) */
      })
  }
}
